/**
 * File: useDiagnosticsPageController.ts
 * Path: apps/admin/src/features/diagnostics/hooks/useDiagnosticsPageController.ts
 * Description: Everything the Diagnostics page needs: this device's checks, the server's checks (admins),
 * the errors this browser recorded, the headline over all of them, and the check-again / copy-report actions.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { systemChecks } from "@idk/config";
import { ROLE_LABELS } from "@idk/types";
import { toast } from "@idk/ui";
import { useAuth } from "@/modules/auth/components/AuthProvider";
import { useRole } from "@/modules/auth/hooks/useRoleQueries";
import { usePushDevices } from "@/shared/hooks/useNotificationsState";
import { thisDeviceId } from "@/shared/services/pushClientService";
import { deviceLabel, readBrowserFacts } from "@/shared/utils/deviceUtils";
import { useSystemReport } from "@/features/diagnostics/hooks/useSystemReportState";
import { deviceChecks, type DeviceFacts } from "@/features/diagnostics/utils/deviceChecksUtils";
import { readDeviceFacts } from "@/features/diagnostics/utils/readDeviceFactsUtils";
import { clearErrorLog, readErrorLog, type LoggedError } from "@/shared/utils/errorLogUtils";
import { reportText } from "@/features/diagnostics/utils/reportUtils";
import { headlineOf } from "@/features/diagnostics/utils/diagnosticsViewUtils";

export const useDiagnosticsPageController = () => {
  const { user } = useAuth();
  const { role, can } = useRole();
  const isAdmin = can("logs");
  const devices = usePushDevices();
  const system = useSystemReport(isAdmin);
  const [facts, setFacts] = useState<DeviceFacts | null>(null);
  const [errors, setErrors] = useState<LoggedError[]>(readErrorLog);
  const [now, setNow] = useState(() => Date.now());

  const mine = thisDeviceId();
  const thisDevice = useMemo(() => devices.data.find((d) => d.id === mine) ?? null, [devices.data, mine]);

  const checkDevice = useCallback(async () => {
    const next = await readDeviceFacts(thisDevice);
    setFacts(next);
    setNow(Date.now());
  }, [thisDevice]);

  useEffect(() => {
    if (devices.loading) return;
    let live = true;
    readDeviceFacts(thisDevice).then((next) => {
      if (!live) return;
      setFacts(next);
      setNow(Date.now());
    });
    return () => {
      live = false;
    };
  }, [devices.loading, thisDevice]);

  const device = facts ? deviceChecks(facts, now) : [];
  const server = system.report ? systemChecks(system.report) : null;
  const all = [...device, ...(server ?? [])];
  const headline = headlineOf(all, !facts || system.loading);

  const runAgain = () => {
    void checkDevice();
    if (isAdmin) void system.run();
    setErrors(readErrorLog());
  };

  const copy = async () => {
    const browser = readBrowserFacts();
    const text = reportText({
      now: Date.now(),
      who: user?.email ?? user?.uid ?? "unknown",
      role: role ? ROLE_LABELS[role] : "unknown",
      device: deviceLabel(browser),
      deviceChecks: device,
      systemChecks: server,
      systemError: system.error,
      errors,
    });
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Report copied. Paste it wherever you're asking for help.");
    } catch {
      toast.error("Couldn't copy here. Try on a computer.");
    }
  };

  const clearErrors = () => {
    clearErrorLog();
    setErrors([]);
  };

  return {
    isAdmin,
    devices,
    system,
    facts,
    device,
    server,
    errors,
    headline,
    checking: !facts || system.loading,
    runAgain,
    copy,
    clearErrors,
  };
};
